/**
 * Verified-identity self-enrollment (`account-security`): the caller's
 * step-up runs `start(proof)`, the page embeds the ID check with the capture
 * token, and once the check ends this polls the security status until the
 * enrollment is decided. Hosts own the timer and the step-up dialog.
 */

import type { IdentityEnrollmentStart } from '@dealr/api-account';
import type { SecurityStatus, StepUpProof } from '@dealr/core-account/domain';
import { enrollmentResultMessage, identityBlock, type IdentityStatusBlock } from '@dealr/core-account';
import { createStateBox, type Observable } from './store';
import { flowErrorMessage } from './stepUp';

export type IdentityEnrollmentPhase = 'idle' | 'capture' | 'checking' | 'passed' | 'failed' | 'sent';

export interface IdentityEnrollmentState {
  phase: IdentityEnrollmentPhase;
  /** `capture`: mount `dealr-ng-identity-check` with this token. */
  captureToken: string | null;
  /** What the card says now (also for a polite live region). */
  message: string;
  /** The latest status block, once a poll has read one. */
  block: IdentityStatusBlock | null;
  /** The ID's name differs from the verified legal name: offer the legal name change. */
  nameChange: boolean;
}

export interface IdentityEnrollmentDeps {
  start(body: { step_up: StepUpProof; channel: 'in_app' }): PromiseLike<IdentityEnrollmentStart>;
  status(): PromiseLike<SecurityStatus>;
  delay(ms: number): PromiseLike<unknown>;
  /** Status polls after the check ends before giving up (2s apart). */
  maxPolls?: number;
}

export interface IdentityEnrollmentFlow extends Observable<IdentityEnrollmentState> {
  /** The step-up's action: starts the check. Rejections stay in the step-up dialog. */
  start(proof: StepUpProof): Promise<void>;
  /** The embedded check ended (or "I’ve finished"): read the decision. */
  captureEnded(): Promise<void>;
  reset(): void;
  destroy(): void;
}

const IDLE: IdentityEnrollmentState = { phase: 'idle', captureToken: null, message: '', block: null, nameChange: false };

export function createIdentityEnrollmentFlow(deps: IdentityEnrollmentDeps): IdentityEnrollmentFlow {
  const box = createStateBox<IdentityEnrollmentState>({ ...IDLE });
  let run = 0;
  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    async start(proof) {
      run++;
      const reply = await deps.start({ step_up: proof, channel: 'in_app' });
      const token = reply && reply.enrollment ? reply.enrollment.capture_token : null;
      box.set(
        token
          ? { phase: 'capture', captureToken: token, message: 'Take a photo of your ID and a short video selfie. Your phone works best; scan the code if you’re on a computer.', nameChange: false }
          : { phase: 'sent', captureToken: null, message: 'We sent you a link to finish the check on your phone.', nameChange: false },
      );
    },
    async captureEnded() {
      if (box.state.phase !== 'capture' && box.state.phase !== 'sent') return;
      const mine = ++run;
      box.set({ phase: 'checking', message: 'Checking your result…' });
      const polls = deps.maxPolls ?? 20;
      try {
        for (let i = 0; i < polls && mine === run; i++) {
          const block = identityBlock(await deps.status());
          if (mine !== run) return;
          const result = block && block.enrollment;
          // The enrollment's own result decides: a re-enrollment can fail while an older record still stands.
          if (result && result.status !== 'pending' && result.status !== 'passed') {
            const view = enrollmentResultMessage(result);
            box.set({ phase: 'failed', block, captureToken: null, nameChange: !!view && view.nameChange, message: view ? view.message : 'The check didn’t pass. You can start a new one.' });
            return;
          }
          if (block && block.verified && (!result || result.status === 'passed')) {
            box.set({ phase: 'passed', block, captureToken: null, message: 'Identity verified. Account recovery is now faster and safer for you.' });
            return;
          }
          await deps.delay(2000);
        }
        if (mine === run) box.set({ phase: 'idle', captureToken: null, message: 'Your check is still being reviewed. This page will show the result when it’s ready.' });
      } catch (error) {
        if (mine === run) box.set({ phase: 'idle', captureToken: null, message: flowErrorMessage(error, 'We couldn’t read the result. Refresh the page in a moment.') });
      }
    },
    reset() {
      run++;
      box.set({ ...IDLE });
    },
    destroy() {
      run++;
    },
  };
}
